import { Button, Card, Comment, Divider, Dropdown, Menu, Space, Tag } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import React from 'react'
import { SearchableDropdown } from './SearchableDropdown'
import { useUpdateTalentCompanyPerson } from '../hooks/api/useEmployees'
import { useGetLookup } from '../hooks/api/useLookups'
import { useGetPerson } from '../hooks/api/usePerson'

const DEFAULT_LOW_OPTIONS = ['LoW 1', 'LoW 2', 'LoW 3', 'LoW 4', 'LoW 5']
const DEFAULT_BAND_OPTIONS = ['GBF1', 'GBF2', 'GBF3', 'GBF4', 'GBF5', 'GBF6', 'C4#', 'TN02']

export const ProfileCard = ({ person, isPdf, canEdit = false }) => {
  const updateTalentCompanyPerson = useUpdateTalentCompanyPerson()
  const lowLookups = useGetLookup({ group: 'Dynamic', key: 'LevelOfWork' })
  const bandLookups = useGetLookup({ group: 'Dynamic', key: 'Band' })
  const personQuery = useGetPerson({ authenticated: !isPdf })

  if (!person) {
    return null
  }

  const employeeUid = person.companyPersonUid || person.uid || person.employment?.companyPersonUid

  const isViewingSelf = Boolean(
    personQuery.data?.companyPerson?.uid &&
    (personQuery.data.companyPerson.uid === employeeUid ||
      personQuery.data.companyPerson.uid === person?.companyPersonUid ||
      personQuery.data.companyPerson.uid === person?.uid)
  )

  const isTalentManager = Boolean(
    personQuery.data?.companyPerson?.admin?.roles?.includes('talent.manager')
  )

  const canEditFields = Boolean(canEdit && isTalentManager && !isViewingSelf && !isPdf)

  const firstName = person.person?.firstName || person.firstName || ''
  const lastName = person.person?.lastName || person.lastName || ''
  const fullName = `${firstName} ${lastName}`.trim() || 'Employee'
  const avatarUrl = person.person?.imageUrl || (firstName ? `https://api.dicebear.com/7.x/initials/svg?seed=${firstName[0]}` : undefined)

  const jobTitle = person.employment?.jobTitle || ''
  const timeInRoleOrStartDate = person.attributes?.timeInRole ? person.attributes.timeInRole : person.employment?.startDate
  const duration = timeInRoleOrStartDate ? dayjs(timeInRoleOrStartDate).from(dayjs(), true) : ''
  const jobHeader = [jobTitle, duration].filter(Boolean).join(' | ')

  const levelOfWorkDisplay = isPdf
    ? (person.talentCompanyPerson?.levelOfWork?.replace(/^LoW\s*/i, '') || person.talentCompanyPerson?.levelOfWork || 'NA')
    : (person.talentCompanyPerson?.levelOfWork || 'NA')

  const payScaleGroupDisplay = person.talentCompanyPerson?.payScaleGroup || 'NA'

  const lowOptions = Array.from(new Set([
    ...(lowLookups.data || []),
    ...DEFAULT_LOW_OPTIONS,
    ...(person.talentCompanyPerson?.levelOfWork ? [person.talentCompanyPerson.levelOfWork] : [])
  ])).filter(Boolean)

  const bandOptions = Array.from(new Set([
    ...(bandLookups.data || []),
    ...DEFAULT_BAND_OPTIONS,
    ...(person.talentCompanyPerson?.payScaleGroup ? [person.talentCompanyPerson.payScaleGroup] : [])
  ])).filter(Boolean)

  return (
    <Card>
      <Comment
        author={fullName}
        avatar={avatarUrl}
        content={jobHeader}
      />
      <Space align='center' wrap>
        {person.talentCompanyPerson?.jobLocation && <Tag color='orange'>{person.talentCompanyPerson.jobLocation}</Tag>}
        {person.attributes?.department && <Tag color='red'>{person.attributes.department}</Tag>}
        {person.attributes?.GRFSubDisciplineName && <Tag color='blue'>{person.attributes.GRFSubDisciplineName}</Tag>}
      </Space>
      <Divider />
      <div className='flex w-full'>
        <div className='w-1/2 flex flex-col gap-2'>
          <div className='flex w-full gap-2 items-center'>
            <p className='w-1/4 text-gray-400 mb-0'>LoW: </p>
            <div className='flex items-center gap-1.5'>
              <p className='mb-0'>{levelOfWorkDisplay}</p>
              {canEditFields && (
                <Dropdown
                  overlay={
                    <Menu
                      style={{ maxHeight: 220, overflowY: 'auto' }}
                      onClick={({ key }) => {
                        if (employeeUid) {
                          updateTalentCompanyPerson.mutate({
                            employeeUid,
                            data: { levelOfWork: key }
                          })
                        }
                      }}
                    >
                      {lowOptions.map(option => (
                        <Menu.Item key={option}>{option}</Menu.Item>
                      ))}
                    </Menu>
                  }
                  trigger={['click']}
                >
                  <DownOutlined className='text-xs text-gray-400 hover:text-blue-500 cursor-pointer' />
                </Dropdown>
              )}
            </div>
          </div>
          <div className='flex w-full gap-2 items-center'>
            <p className='w-1/4 text-gray-400 mb-0'>Band: </p>
            <div className='flex items-center gap-1.5'>
              <p className='mb-0'>{payScaleGroupDisplay}</p>
              {canEditFields && (
                <SearchableDropdown
                  options={bandOptions}
                  placeholder='Search band...'
                  emptyText='No matching bands'
                  onSelect={(key) => {
                    if (employeeUid) {
                      updateTalentCompanyPerson.mutate({
                        employeeUid,
                        data: { payScaleGroup: key }
                      })
                    }
                  }}
                >
                  <DownOutlined className='text-xs text-gray-400 hover:text-blue-500 cursor-pointer' />
                </SearchableDropdown>
              )}
            </div>
          </div>
          <div className='flex w-full gap-2'>
            <p className='w-1/4 text-gray-400'>Email: </p><p>{person.email || 'Unset'}</p>
          </div>
          {!isPdf && (
            <div className='flex w-full gap-2'>
              <p className='w-1/4 text-gray-400'>Gender: </p><p>{person.sex || 'Unset'}</p>
            </div>
          )}
          {!isPdf && (
            <div className='flex w-full gap-2'>
              <p className='w-1/4 text-gray-400'>Race: </p><p>{person.race || 'Unset'}</p>
            </div>
          )}
        </div>
        <div className='w-1/2 flex flex-col gap-2'>
          {!isPdf && (
            <div className='flex w-full gap-2'>
              <p className='w-1/4 text-gray-400'>Age: </p><p>{person.dateOfBirth ? dayjs().diff(dayjs(person.dateOfBirth), 'years') : 'Unset'}</p>
            </div>
          )}
          {!isPdf && (
            <div className='flex w-full gap-2'>
              <p className='w-1/4 text-gray-400'>Date of hire: </p><p>{person.employment?.startDate ? dayjs(person.employment.startDate).format('DD/MM/YYYY') : 'Unset'}</p>
            </div>
          )}
          {!isPdf && (
            <div className='flex w-full gap-2'>
              <p className='w-1/4 text-gray-400'>Years to retirement: </p><p>{person.attributes?.retirementDate ? dayjs(person.attributes.retirementDate).diff(dayjs(), 'years') : (person.dateOfBirth ? 60 - dayjs().diff(dayjs(person.dateOfBirth), 'years') : 'Unset')}</p>
            </div>
          )}
          <div className='flex w-full gap-2'>
            <p className='w-1/4 text-gray-400'>Manager: </p>
            <p>
              {person.employment?.jobManager?.companyPerson
                ? (
                  <Button className='p-0' href={person.employment.jobManager.companyPerson.uid} size='small' type='link'>
                    {person.employment.jobManager.companyPerson.person?.firstName} {person.employment.jobManager.companyPerson.person?.lastName}
                  </Button>
                  )
                : (
                    'None'
                  )}
            </p>
          </div>
          <div className='flex w-full gap-2'>
            <p className='w-1/4 text-gray-400'>MoR: </p>
            <p>
              {person.employment?.jobManager?.jobManager?.companyPerson?.person
                ? (
                  <Button className='p-0' href={person.employment.jobManager.jobManager.companyPerson.uid} type='link' size='small'>
                    {`${person.employment.jobManager.jobManager.companyPerson.person.firstName} ${person.employment.jobManager.jobManager.companyPerson.person.lastName}`}
                  </Button>
                  )
                : (
                    'None'
                  )}
            </p>
          </div>
        </div>
      </div>
    </Card>
  )
}
